import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  FiAward, FiBell, FiBellOff, FiCheck, FiChevronDown, FiFilm, FiHome, FiLogOut,
  FiMessageCircle, FiPlus, FiRadio, FiSearch, FiShoppingBag, FiSlash, FiUser, FiUsers, FiX,
} from 'react-icons/fi';
import { cCategories, fullUrl } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import LanguageSwitcher from '../LanguageSwitcher';
import SoundToggle from '../SoundToggle';
import ThemeToggle from '../ThemeToggle';
import CategoryDrawer from './CategoryDrawer';
import UserAvatar from './UserAvatar';
import { BLUE } from './brand';

/** Top navbar: Article · Category Post (dropdown) · Shop + search and badges. */
export default function CommunityNav() {
  const { t, lang } = useLanguage();
  const { user, unread, openAuth, logout, alerts, enableAlerts, channels, actingChannel,
    setActingChannel } = useCommunity();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [cats, setCats] = useState([]);
  const [catOpen, setCatOpen] = useState(false);
  const [meOpen, setMeOpen] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    cCategories().then((rows) => setCats(Array.isArray(rows) ? rows : [])).catch(() => {});
  }, []);

  useEffect(() => { setCatOpen(false); setMeOpen(false); }, [pathname, search]);

  useEffect(() => {
    setQ(new URLSearchParams(search).get('q') || '');
  }, [search]);

  const activeCat = new URLSearchParams(search).get('category_id');

  const submitSearch = (e) => {
    e.preventDefault();
    const term = q.trim();
    navigate(term ? `/?q=${encodeURIComponent(term)}` : '/');
  };

  const linkClass = ({ isActive }) =>
    `px-3 py-2 rounded-xl text-sm font-bold transition ${
      isActive ? 'text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
    }`;

  const activeStyle = ({ isActive }) => (isActive ? { background: BLUE } : undefined);

  return (
    <header className="sticky top-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-100 dark:border-gray-800">
      <div className="mx-auto max-w-[1500px] px-3 lg:px-4 h-14 flex items-center gap-2">
        {/* Hamburger — every category + the “General” menu in one drawer */}
        <CategoryDrawer />

        <Link to="/" className="flex items-center gap-2 shrink-0" title={t('communityTitle')}>
          <img src={`${process.env.PUBLIC_URL}/ckl.png`} alt="CodeKhmerLearning" className="w-8 h-8" />
          <span className="font-extrabold text-[15px] sm:text-lg tracking-tight hidden sm:inline whitespace-nowrap" style={{ color: BLUE }}>
            CodeKhmerLearning
          </span>
          <span className="hidden md:inline text-[10px] font-extrabold px-1.5 py-0.5 rounded-md text-white whitespace-nowrap" style={{ background: BLUE }}>
            {t('communityBadge')}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-0.5 ml-2">
          <NavLink to="/" end className={linkClass} style={activeStyle}>
            <span className="inline-flex items-center gap-1.5">
              <FiHome className="w-4 h-4" /> {t('articleNav')}
            </span>
          </NavLink>

          <div className="relative">
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className="px-3 py-2 rounded-xl text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 inline-flex items-center gap-1.5"
            >
              {t('categoryPost')} <FiChevronDown className="w-4 h-4" />
            </button>
            {catOpen && (
              <div className="absolute z-40 mt-1 w-60 rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-100 dark:border-gray-800 p-2 anim-pop">
                <Link to="/" className="flex items-center justify-between px-3 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold">
                  {t('allCategories')}
                </Link>
                {cats.map((c) => (
                  <Link
                    key={c.id}
                    to={`/?category_id=${c.id}`}
                    className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${
                      String(c.id) === activeCat ? 'font-bold' : ''
                    }`}
                    style={String(c.id) === activeCat ? { color: BLUE } : undefined}
                  >
                    <span>{c.icon} {lang === 'kh' ? (c.name_km || c.name) : c.name}</span>
                    <span className="text-[11px] text-gray-400">{c.post_count ?? 0}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <NavLink to="/shop" className={linkClass} style={activeStyle}>
            <span className="inline-flex items-center gap-1.5">
              <FiShoppingBag className="w-4 h-4" /> {t('shopNav')}
            </span>
          </NavLink>

          {/* Reel — the TikTok-style full-screen video feed */}
          <NavLink to="/reels" className={linkClass} style={activeStyle}>
            <span className="inline-flex items-center gap-1.5">
              <FiFilm className="w-4 h-4" /> {t('reelNav')}
            </span>
          </NavLink>

          {/* Leaderboard — most articles / likes / active / followers */}
          <NavLink to="/leaderboard" className={linkClass} style={activeStyle}>
            <span className="inline-flex items-center gap-1.5">
              <FiAward className="w-4 h-4" /> {t('leaderboard')}
            </span>
          </NavLink>

          {/* Channels — create up to 3 + subscribe (“ជាវ”) to other members' */}
          <NavLink to="/channels" className={linkClass} style={activeStyle}>
            <span className="inline-flex items-center gap-1.5">
              <FiRadio className="w-4 h-4" /> {t('channelsNav')}
            </span>
          </NavLink>

          {/* Groups — public/private chat groups with invite links */}
          <NavLink to="/groups" className={linkClass} style={activeStyle}>
            <span className="inline-flex items-center gap-1.5">
              <FiUsers className="w-4 h-4" /> {t('groupsNav')}
            </span>
          </NavLink>
        </nav>

        {/* Reel + Shop buttons between sm and md (below sm the phone bottom bar
            already has them, which leaves room for the hamburger). */}
        <Link
          to="/reels"
          className="hidden sm:inline-flex md:hidden items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-bold text-white shrink-0"
          style={{ background: BLUE }}
        >
          <FiFilm className="w-3.5 h-3.5" /> {t('reelNav')}
        </Link>
        <Link
          to="/shop"
          className="hidden sm:inline-flex md:hidden items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-bold shrink-0 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
        >
          <FiShoppingBag className="w-3.5 h-3.5" /> {t('shopNav')}
        </Link>

        <form onSubmit={submitSearch} className="flex-1 min-w-0 hidden sm:flex items-center gap-2 mx-2">
          <div className="flex-1 min-w-0 flex items-center gap-2 rounded-full bg-gray-100 dark:bg-gray-800 px-3.5 py-2">
            <FiSearch className="w-4 h-4 text-gray-400 shrink-0" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('searchCommunity')}
              className="flex-1 min-w-0 bg-transparent outline-none text-sm dark:text-gray-100"
            />
            {q && (
              <button type="button" onClick={() => { setQ(''); navigate('/'); }} aria-label="Clear" className="text-gray-400">
                <FiX className="w-4 h-4" />
              </button>
            )}
          </div>
        </form>

        <div className="flex items-center gap-0.5 ml-auto">
          {/* Button sounds + dark/light mode — always visible, also on phones */}
          <SoundToggle />
          <ThemeToggle />
          <div className="hidden lg:flex items-center gap-0.5">
            <LanguageSwitcher />
          </div>

          <IconBadge to="/notifications" count={unread.notifications} label={t('notificationNav')}>
            <FiBell className="w-5 h-5" />
          </IconBadge>
          <IconBadge to="/messages" count={unread.messages} label={t('messageNav')}>
            <FiMessageCircle className="w-5 h-5" />
          </IconBadge>

          {user ? (
            <div className="relative">
              <button type="button" onClick={() => setMeOpen((v) => !v)} className="flex items-center gap-1.5 p-1 rounded-full">
                <UserAvatar
                  user={actingChannel
                    ? { name: actingChannel.name, avatar: actingChannel.avatar,
                        username: actingChannel.nickname || actingChannel.slug }
                    : user}
                  size={32}
                />
                <FiChevronDown className="w-4 h-4 text-gray-400 hidden sm:block" />
              </button>
              {meOpen && (
                <div className="absolute right-0 z-40 mt-1 w-64 rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-100 dark:border-gray-800 p-2 anim-pop">
                  {/* ── switch account: my account ⇄ my channels ── */}
                  <p className="px-2 pb-1 pt-0.5 text-[10px] font-extrabold uppercase text-gray-400">
                    {t('switchAccount')}
                  </p>
                  <button
                    type="button"
                    onClick={() => { setActingChannel(0); setMeOpen(false); }}
                    className={`w-full flex items-center gap-2 px-2 py-2 rounded-xl text-sm ${
                      actingChannel ? 'hover:bg-gray-100 dark:hover:bg-gray-800' : 'bg-blue-50 text-blue-700 font-bold'
                    }`}
                  >
                    <UserAvatar user={user} size={26} />
                    <span className="min-w-0 flex-1 truncate text-left">
                      {t('personalAccount')} <span className="text-gray-400">@{user.username}</span>
                    </span>
                    {!actingChannel && <FiCheck className="w-4 h-4 shrink-0" />}
                  </button>
                  {channels.map((ch) => (
                    <button
                      key={ch.id}
                      type="button"
                      onClick={() => { setActingChannel(ch.id); setMeOpen(false); }}
                      className={`w-full flex items-center gap-2 px-2 py-2 rounded-xl text-sm ${
                        actingChannel && actingChannel.id === ch.id
                          ? 'bg-blue-50 text-blue-700 font-bold'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      {ch.avatar ? (
                        <img src={fullUrl(ch.avatar)} alt={ch.name} className="w-[26px] h-[26px] rounded-lg object-cover shrink-0" />
                      ) : (
                        <span
                          className="w-[26px] h-[26px] rounded-lg flex items-center justify-center text-white text-xs font-extrabold shrink-0"
                          style={{ background: BLUE }}
                        >
                          {(ch.name || 'C').slice(0, 1).toUpperCase()}
                        </span>
                      )}
                      <span className="min-w-0 flex-1 truncate text-left">{ch.name}</span>
                      <span className="text-[10px] text-gray-400 shrink-0">@{ch.nickname || ch.slug}</span>
                      {actingChannel && actingChannel.id === ch.id && <FiCheck className="w-4 h-4 shrink-0" />}
                    </button>
                  ))}
                  <Link
                    to="/channels"
                    className="flex items-center gap-2 px-2 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <span className="w-[26px] h-[26px] rounded-lg border border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center shrink-0">
                      <FiPlus className="w-3.5 h-3.5" />
                    </span>
                    {t('createChannelShort')}
                  </Link>
                  <div className="my-1 border-t border-gray-100 dark:border-gray-800" />

                  <Link to="/me" className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800">
                    <FiUser className="w-4 h-4" /> {t('profileNav')}
                  </Link>
                  <Link to="/leaderboard" className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800">
                    <FiAward className="w-4 h-4" /> {t('leaderboard')}
                  </Link>
                  <Link to="/me?tab=posts" className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800">
                    <FiShoppingBag className="w-4 h-4" /> {t('myPosts')}
                  </Link>
                  {/* my channels (max 3) · my groups · the ✓ mark · blocks */}
                  <Link to="/channels" className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800">
                    <FiRadio className="w-4 h-4" /> {t('channelMyChannels')}
                  </Link>
                  <Link to="/groups" className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800">
                    <FiUsers className="w-4 h-4" /> {t('groupMyGroups')}
                  </Link>
                  <Link to="/verify" className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800">
                    <img
                      src={`${process.env.PUBLIC_URL}/verify/verify_check.png`}
                      alt={t('verifyNav')}
                      className="w-4 h-4 object-contain"
                    />
                    {t('verifyNav')}
                  </Link>
                  <Link to="/blocked" className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800">
                    <FiSlash className="w-4 h-4" /> {t('blockedNav')}
                  </Link>
                  <Link to="/messages" className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800">
                    <FiMessageCircle className="w-4 h-4" /> {t('messageNav')}
                    {unread.messages > 0 && (
                      <span className="ml-auto text-[11px] font-bold" style={{ color: BLUE }}>{unread.messages}</span>
                    )}
                  </Link>

                  {/* Browser notifications — asks permission the first time */}
                  <button
                    type="button"
                    data-sfx="toggle"
                    onClick={() => enableAlerts()}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                    aria-pressed={alerts === 'granted'}
                    title={t('alertsHint')}
                  >
                    {alerts === 'granted'
                      ? <FiBell className="w-4 h-4" />
                      : <FiBellOff className="w-4 h-4" />}
                    {alerts === 'granted' ? t('alertsOn') : t('enableAlerts')}
                    <span className={`ml-auto w-9 h-5 rounded-full relative transition ${alerts === 'granted' ? 'bg-[#0B5FDB]' : 'bg-gray-300 dark:bg-gray-600'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${alerts === 'granted' ? 'left-[18px]' : 'left-0.5'}`} />
                    </span>
                  </button>

                  {/* Sound + dark/light mode — labelled so phone users can find them */}
                  <SoundToggle withLabel />
                  <ThemeToggle withLabel />
                  <div className="px-1 py-0.5 lg:hidden">
                    <LanguageSwitcher />
                  </div>
                  <button
                    type="button"
                    onClick={() => { logout(); navigate('/'); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-red-600 hover:bg-red-50 dark:hover:bg-gray-800"
                  >
                    <FiLogOut className="w-4 h-4" /> {t('signOut')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => openAuth('signin')}
              className="ml-1 px-3.5 py-2 rounded-full text-white text-xs sm:text-sm font-bold"
              style={{ background: BLUE }}
            >
              {t('signIn')}
            </button>
          )}
        </div>
      </div>

      {/* The account switcher is on: everything is published as the channel. */}
      {actingChannel && (
        <ActingAsBar
          channel={actingChannel}
          onStop={() => setActingChannel(0)}
          label={t('postingAs').replace('{name}', actingChannel.name)}
          hint={t('channelAccountHint')}
          stop={t('switchBack')}
        />
      )}
    </header>
  );
}

/** The “acting as a channel” bar under the navbar. */
const ACTING_AVATAR = 'w-6 h-6 rounded-lg object-cover shrink-0';

function ActingAsBar({ channel, onStop, label, hint, stop }) {
  return (
    <div className="border-t border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-900/20">
      <div className="mx-auto max-w-[1500px] px-3 lg:px-4 py-1.5 flex items-center gap-2 text-xs">
        {channel.avatar ? (
          <img src={fullUrl(channel.avatar)} alt={channel.name} className={ACTING_AVATAR} />
        ) : (
          <span
            className="w-6 h-6 rounded-lg flex items-center justify-center text-white text-[11px] font-extrabold shrink-0"
            style={{ background: BLUE }}
          >
            {(channel.name || 'C').slice(0, 1).toUpperCase()}
          </span>
        )}
        <span className="font-extrabold truncate text-amber-900 dark:text-amber-200">{label}</span>
        <span className="hidden sm:inline text-amber-700/80 dark:text-amber-300/80 truncate">{hint}</span>
        <button
          type="button"
          onClick={onStop}
          className="ml-auto shrink-0 font-bold underline text-amber-900 dark:text-amber-200"
        >
          {stop}
        </button>
      </div>
    </div>
  );
}

function IconBadge({ to, count, label, children }) {
  return (
    <Link
      to={to}
      title={label}
      className="relative p-2 rounded-xl text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
    >
      {children}
      {count > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
          style={{ background: BLUE }}
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  );
}
